import Vue from 'vue';

import Type from '@/store/mutation-type';
import ctt from '@/utils/const';
import { unconnectTime, unconnectTimeByType } from '@/utils/dict';
import { timer } from '@/utils/utils';
import { isArray, isEmpty, isObject } from '@/utils/is';
import { isDevice, isOffice } from '@/utils/deviceUtils';
import { toBmapPos2 } from '@/utils/bmapUtil';

// 需要进行百度坐标转换的设备类型
const BAIDU_COORD_DEVICE_TYPES = [
  ctt.MN_606,
  ctt.CD_602,
  ctt.CD_603,
  ctt.GD_606,
  ctt.GD_612,
  ctt.BC500,
  ctt.PTQXZ,
  ctt.BU_10,
  ctt.GW1021,
  ctt.GW1034M,
  ctt.GW1041,
  ctt.B02_V2,
  ctt.HT300,
  ctt.RM1000
];

// 设备状态优先级映射：mState -> 直接赋值的状态
const STATE_PRIORITY_MAP = {
  [ctt.POINT_STATE_WARN]: ctt.POINT_STATE_WARN,
  [ctt.POINT_STATE_DANGER]: ctt.POINT_STATE_DANGER,
  [ctt.POINT_STATE_HIGH_RISK]: ctt.POINT_STATE_HIGH_RISK
};

// 批量更新时需要同步的属性
const BATCH_UPDATE_FIELDS = [
  'state',
  'state2',
  'name',
  'modState',
  'runstate',
  'beginTime',
  'beginTimeD',
  'latitude',
  'longitude',
  'altitude',
  'batteryPercent',
  'isCharging'
];

const upDeviceMap = {};

/**
 * 根据 mState 和 runstate 计算设备最终状态
 * @param {number} mState - 消息状态
 * @param {number} runstate - 运行状态
 * @param {number} currentState - 当前状态
 * @returns {number} 最终状态
 */
function resolveDeviceState(mState, runstate, currentState) {
  // 高优先级状态直接返回
  if (STATE_PRIORITY_MAP[mState]) {
    return STATE_PRIORITY_MAP[mState];
  }

  switch (runstate) {
    case ctt.RUNSTATE_FAULT:
      return ctt.POINT_STATE_FAULT;
    case ctt.RUNSTATE_COLLECT:
      return ctt.POINT_STATE_COLLECT;
    case ctt.RUNSTATE_FIX:
      return mState === ctt.POINT_STATE_CONNECT ? ctt.POINT_STATE_FIX : mState;
    case ctt.RUNSTATE_NORMAL:
      return mState;
    default:
      return currentState;
  }
}

/**
 * 提取危险状态的模块类型
 * @param {Object} modState - 模块状态对象
 * @returns {string[]} 危险模块类型数组
 */
function extractDangerTypes(modState) {
  if (!modState) return [];
  const types = [];
  for (const k in modState) {
    if (modState[k].state === ctt.POINT_STATE_DANGER) {
      types.push(k);
    }
  }
  return types;
}

export default {
  [Type.SET_VUE_SOCKET_IO](state, ws) {
    state.webSocket = ws;
  },
  [Type.UPDATE_DEVICE_TREE](state, msg) {
    timer(() => {
      if ([ctt.GW1021, ctt.GW1034M].includes(msg?.deviceTypeCode)) {
        msg.deviceType = msg.deviceTypeCode;
      }
      this.commit(`socket/${Type.JUDGE_DEVICE_UNCONNECT}`, msg);

      let item = state.deviceSNMap[msg.sn];
      if (!item) return;
      item = upDeviceMap[msg.sn] = {
        name: item.name,
        state: item.state,
        types: item.types,
        latitude: item.latitude,
        longitude: item.longitude,
        altitude: item.altitude,
        batteryPercent: item.batteryPercent,
        isCharging: item.isCharging
      };
      const {
        state: oldState,
        name: oldName,
        beginTime: oldBeginTime,
        beginTimeD: oldBeginTimeD,
        latitude: oldLatitude,
        longitude: oldLongitude,
        altitude: oldAltitude,
        batteryPercent: oldBatteryPercent,
        isCharging: oldIsCharging
      } = item;
      let {
        state: mState,
        deviceType,
        deviceTypeCode,
        runstate,
        name,
        modState,
        beginTime,
        beginTimeD,
        latitude,
        longitude,
        altitude,
        batteryPercent,
        isCharging
      } = msg;

      // 坐标转换：特定设备类型需要转换为百度坐标
      const deviceTypeKey = deviceType || deviceTypeCode;
      if (BAIDU_COORD_DEVICE_TYPES.includes(deviceTypeKey) && (longitude || latitude)) {
        [longitude, latitude] = toBmapPos2({ coordinate: [longitude, latitude] });
      }

      // 计算并更新设备状态
      const newState = resolveDeviceState(mState, runstate, item.state);
      if (item.state !== newState) {
        item.state = newState;
      }

      // 提取并更新危险模块类型
      const types = extractDangerTypes(modState);
      const typesChanged = !item.types || JSON.stringify(item.types) !== JSON.stringify(types);
      if (typesChanged) {
        item.types = types;
      }
      if (item.name !== name) {
        // 修改名称
        item.name = name;
      }

      // 标识是否需要更新
      const hasChanged =
        item.name !== oldName ||
        item.state !== oldState ||
        beginTime !== oldBeginTime ||
        beginTimeD !== oldBeginTimeD ||
        batteryPercent !== oldBatteryPercent ||
        isCharging !== oldIsCharging;

      if (hasChanged) {
        item.change = true;
        Object.assign(item, {
          state2: mState,
          modState,
          runstate,
          beginTime,
          beginTimeD,
          batteryPercent,
          isCharging
        });
      }

      // 更新坐标（仅在有坐标且坐标变化时更新）
      const hasPosition = longitude || latitude;
      const positionChanged =
        latitude !== oldLatitude || longitude !== oldLongitude || altitude !== oldAltitude;

      if (hasPosition && positionChanged) {
        Object.assign(item, { latitude, longitude, altitude });
      }
    });
  },
  [Type.INTERVAL_UP](state) {
    // 定时批量更新设备数据
    if (state.timer) clearInterval(state.timer);
    state.timer = setInterval(() => {
      Object.keys(upDeviceMap).forEach(sn => {
        const item = upDeviceMap[sn];
        const target = state.deviceSNMap[sn];

        // 清理已不存在的设备
        if (!target) {
          delete upDeviceMap[sn];
          return;
        }

        // 只更新有变化的设备
        if (!item.change) return;
        item.change = false;

        // 批量更新属性
        BATCH_UPDATE_FIELDS.forEach(field => {
          Vue.set(target, field, item[field]);
        });
      });
    }, 1000);
  },
  [Type.GET_DEVICE_DETAIL](state, data) {
    if (state.singleDeviceDetail.beginTime !== data.beginTime) {
      // 避免出现重复数据
      state.singleDeviceDetail = data;
    }
  },
  [Type.GET_DEVICE_REPORT](state, data) {
    const len = state.devicesReport.length;
    if (len > 200) state.devicesReport.splice(len / 2);
    state.devicesReport.unshift(data);
  },
  [Type.UPDATE_DEVICE_TREE_LANG](state, data) {
    if (!data) return;

    // 需要更新的基础属性
    const BASE_FIELDS = ['name', 'officeName', 'deviceTypeName'];

    function traverseDeviceTree(nodes) {
      nodes.forEach(node => {
        const target = state.deviceIdMap[node.id];
        if (!target) return;

        // 更新基础属性
        BASE_FIELDS.forEach(field => {
          if (node[field] !== undefined) {
            target[field] = node[field];
          }
        });

        if (isOffice(node.nodeType)) {
          // 递归更新子节点
          if (isArray(node.children) && !isEmpty(node.children)) {
            traverseDeviceTree(node.children);
          }
        } else if (isDevice(node.nodeType)) {
          // 更新设备动态信息
          if (isObject(node.dynamicInfoMap) && !isEmpty(node.dynamicInfoMap)) {
            Object.assign(target.dynamicInfoMap, node.dynamicInfoMap);
          }
        }
      });
    }

    traverseDeviceTree(data);
  },
  [Type.GET_DEVICE_TREE](state, data) {
    if (!data) {
      state.deviceTree = null;
      state.devicesSourseMap = [];
      return;
    }
    const deviceIdMap = {};
    const deviceSNMap = {};
    function sourceMap(d) {
      const sns = [];
      function loop(loopData, url) {
        loopData.forEach(e => {
          if (e.nodeType === 1) {
            deviceIdMap[e.id] = e;
            if (e.children && e.children.length > 0) {
              loop(e.children, e.stationMap);
            }
          } else if (e.nodeType === 2) {
            // 默认离线状态
            e.runstate = 0;
            deviceIdMap[e.id] = e;
            deviceSNMap[e.sn] = e;
            Vue.set(e, 'mapUrl', url);
            sns.push(e);

            // 检测类状态设置为在线
            // if (isDetectDevice(e.runMode)) {
            //   normalizeState(e);
            //   normalizeRunstate(e);
            // }
          }
        });
      }
      loop(d);
      return sns;
    }

    state.deviceTree = data;
    state.devicesSourseMap = sourceMap(data);
    state.deviceIdMap = deviceIdMap;
    state.deviceSNMap = deviceSNMap;

    // 批量转换设备坐标为百度坐标
    Object.values(state.deviceIdMap).forEach(e => {
      if (e.nodeType === 1 || !e.longitude || !e.latitude) return;
      [e.longitude, e.latitude] = toBmapPos2({ coordinate: [e.longitude, e.latitude] });
    });

    if (state.deviceTree.length) state.dataLoading = false;
  },
  [Type.CLEAR_REPORT](state) {
    state.devicesReport.splice(0);
  },
  [Type.SAVE_POINT_ID](state, id) {
    state.allPointIds.push(id);
  },
  [Type.JUDGE_DEVICE_UNCONNECT](state, { runMode, deviceType, sn }) {
    const delay = unconnectTimeByType[deviceType] || unconnectTime[runMode];

    const oldNum = state.onlineDetectionTimerMap[sn];
    if (oldNum) clearTimeout(oldNum);
    const num = setTimeout(() => {
      clearTimeout(oldNum);
      state.onlineDetectionTimerMap[sn] = null;

      // 调试日志：设备心跳断联
      console.log('[心跳断联]---------', sn);

      const item = state.deviceSNMap[sn];
      if (item) {
        Vue.set(item, 'state', ctt.POINT_STATE_UNCONNECT);
        Vue.set(item, 'state2', ctt.POINT_STATE_UNCONNECT);
        Vue.set(item, 'runstate', ctt.POINT_STATE_UNCONNECT);
        if (isObject(item.modState)) {
          Object.values(item.modState).forEach(e => {
            e.state = ctt.POINT_STATE_UNCONNECT;
            e.runstate = ctt.POINT_STATE_UNCONNECT;
          });
          Vue.set(item, 'modState', item.modState);
        }
      }
    }, delay);
    state.onlineDetectionTimerMap[sn] = num;
  },
  [Type.MODIFY_DEVICE_INFO](state, modifyData) {
    Object.assign(state.deviceSNMap[modifyData.sn], modifyData);
  },
  [Type.ADD_SOCKET](state) {
    state.socketNum++;
  },
  [Type.SUB_DEVICE](state, data) {
    Vue.set(state.subDeviceMap, data.sn, data);
  },
  [Type.UNSUB_DEVICE](state, sn) {
    Vue.delete(state.subDeviceMap, sn);
  }
};
